/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file retailTransactionSlice.js
 * @description Redux slice for managing retailTransaction related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the retailTransaction slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = () => ({
  error: null,
  loading: false,
  savedAdjustmentId: undefined,
  saving: false,
  ticketData: null,
  ticketPaidOut: false,
  tillAdjustmentCompleted: false,
});

/**
 * @function loadTicketData
 * @description Async thunk for load ticket data operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const loadTicketData = createAsyncThunk(
  "retailTransaction/loadTicket",
  async ({ betslipReference }, thunkAPI) => {
    try {
      const { authToken, language, lineId, originId, tillAuth } = getRequestParams(thunkAPI.getState());

      const result = await createAxiosInstance(thunkAPI.dispatch, { authToken, language, tillAuth }).get(
        `/retail/tickets?originId=${originId}&lineId=${lineId}&betslipReference=${betslipReference}`,
      );

      return { ticketData: result.data };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to load ticket information", // serializable (err.response.data)
        name: "Load Ticket Details Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  },
);

/**
 * @function postTicketPayout
 * @description Async thunk for post ticket payout operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const postTicketPayout = createAsyncThunk("retailTransaction/payTicket", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId, tillAuth } = getRequestParams(thunkAPI.getState());

    const { amount, betslipId, betslipReference } = data;

    const result = await createAxiosInstance(thunkAPI.dispatch, { authToken, language, tillAuth }).post(
      `/retail/tickets/payout?originId=${originId}&lineId=${lineId}`,
      { amount, betslipId },
    );

    thunkAPI.dispatch(loadTicketData({ betslipReference }));

    return { result };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to pay ticket out", // serializable (err.response.data)
      name: "Pay Ticket Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @function postTillTransaction
 * @description Async thunk for post till transaction operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const postTillTransaction = createAsyncThunk("retailTransaction/tillTransaction", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId, tillAuth } = getRequestParams(thunkAPI.getState());

    const { amount, description, isCashIn, isCashOut } = data;

    const result = await createAxiosInstance(thunkAPI.dispatch, { authToken, language, tillAuth }).post(
      `/retail/tills/adjustment?originId=${originId}&lineId=${lineId}`,
      { cashIn: isCashIn ? amount : 0, cashOut: isCashOut ? amount : 0, description },
    );

    return { transaction: result.data };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to create adjustment", // serializable (err.response.data)
      name: "Till Adjustment Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant retailTransactionSlice
 * @description Redux slice for retailTransaction management
 */
const retailTransactionSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(loadTicketData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadTicketData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(loadTicketData.fulfilled, (state, action) => {
        state.ticketData = action.payload.ticketData;
        state.error = null;
        state.loading = false;
      })

      .addCase(postTicketPayout.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.ticketPaidOut = false;
      })
      .addCase(postTicketPayout.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message;
      })
      .addCase(postTicketPayout.fulfilled, (state, action) => {
        state.error = null;
        state.saving = false;
        state.ticketPaidOut = true;
      })

      .addCase(postTillTransaction.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.tillAdjustmentCompleted = false;
      })
      .addCase(postTillTransaction.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message;
      })
      .addCase(postTillTransaction.fulfilled, (state, action) => {
        state.error = null;
        state.saving = false;
        state.tillAdjustmentCompleted = true;
        state.savedAdjustmentId = action.payload.transaction?.id;
      });
  },
  initialState: getInitialState(),
  name: "retailTransaction",
  // reducers actions
  reducers: {
    //
    clearTicketPaidOutStatus(state) {
      state.error = null;
      state.ticketPaidOut = false;
    },
    //
    clearTillOperationStatus(state) {
      state.error = null;
      state.tillAdjustmentCompleted = false;
      state.savedAdjustmentId = undefined;
    },
  },
});

const { actions, reducer } = retailTransactionSlice;
export const { clearTicketPaidOutStatus, clearTillOperationStatus } = actions;
export default reducer;
